import { GInput } from "components/UI/Inputs/GInput";
import cameraIcon from "assets/svg/cameraIconTransparent.svg";
import { useEffect, useMemo, useState } from "react";
import { CButton } from "components/UI/Button/Buttons";
import { toast } from "sonner";
import OnboardingAvatars from "./OnboardingAvatars";
import { countryList } from "utils/countryList";
import GInputWithDropDown from "components/UI/Inputs/GInputWithDropDown";
import ImageCropperModal from "components/UI/ImageCropperModal/ImageCropperModal";
import {
  UpdateAccountServicePayload,
  MyProfileResponse,
  ProfessionalDescriptionItem,
} from "interfaces";

const OnbordingFirstMainBody = ({
  setIsFirstPage,
  formData,
  setFormData,
  profileData,
  professionalDescriptions,
}: {
  setIsFirstPage: (val: boolean) => void;
  formData: UpdateAccountServicePayload;
  setFormData: React.Dispatch<
    React.SetStateAction<UpdateAccountServicePayload>
  >;
  profileData?: MyProfileResponse;
  professionalDescriptions?: ProfessionalDescriptionItem[];
}) => {
  const [isSelectedImage, setIsSelectedImage] = useState<string | null>(null);
  const [userAvater, setUserAvatar] = useState<string>("");
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const [cropperImageSrc, setCropperImageSrc] = useState("");
  const [filteredListOfCountries, setFilteredListOfCountries] =
    useState<string[]>(countryList);
  const [errors, setErrors] = useState({
    fullName: "",
    jobTitle: "",
    country: "",
  });

  const jobTitleList = useMemo(
    () => professionalDescriptions?.map((d) => d.title) ?? [],
    [professionalDescriptions],
  );

  const [filteredListOfJobTitle, setFilteredListOfJobTitle] =
    useState<string[]>(jobTitleList);

  const updateField = (
    field: keyof UpdateAccountServicePayload,
    value: UpdateAccountServicePayload[keyof UpdateAccountServicePayload],
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  // const convertAvatarToFile = async (
  //   avatarKey: string,
  //   avatarPath: string,
  // ) => {
  //   try {
  //     const response = await fetch(window.location.origin + avatarPath, {
  //       mode: "cors",
  //     });
  //     const text = await response.text();
  //     const blob = new Blob([text], { type: "image/svg+xml" });
  //     const file = new File([blob], avatarKey, { type: "image/svg+xml" });
  //     updateField("Picture", file);
  //   } catch (error) {
  //     console.error("Error converting image to file:", error);
  //   }
  // };

  const convertAvatarToFile = async (
    avatarKey: string,
    avatarPath: string,
  ) => {
    try {
      const response = await fetch(window.location.origin + avatarPath, {
        mode: "cors",
      });
      const svgText = await response.text();

      const svgBlob = new Blob([svgText], { type: "image/svg+xml" });
      const svgUrl = URL.createObjectURL(svgBlob);

      await new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const size = 256;
          const canvas = document.createElement("canvas");
          canvas.width = size;
          canvas.height = size;
          const ctx = canvas.getContext("2d")!;
          ctx.drawImage(img, 0, 0, size, size);
          URL.revokeObjectURL(svgUrl);

          canvas.toBlob((blob) => {
            if (!blob) {
              reject(new Error("Canvas toBlob failed"));
              return;
            }
            const file = new File([blob], `${avatarKey}.png`, {
              type: "image/png",
            });
            updateField("Picture", file);
            resolve();
          }, "image/png");
        };
        img.onerror = () => {
          URL.revokeObjectURL(svgUrl);
          reject(new Error("Image load failed"));
        };
        img.src = svgUrl;
      });
    } catch (error) {
      console.error("Error converting image to file:", error);
    }
  };

  // Restore avatar visual state when component mounts (for back navigation)
  useEffect(() => {
    if (formData.Picture) {
      if (formData.Picture instanceof File) {
        if (formData.Picture.name in OnboardingAvatars) {
          // Preset avatar — restore the border highlight by key
          setUserAvatar(formData.Picture.name);
        } else {
          // User-uploaded image — show preview
          const imageUrl = URL.createObjectURL(formData.Picture);
          setIsSelectedImage(imageUrl);
          setUserAvatar("imagePicked");
        }
      } else if (typeof formData.Picture === "string") {
        setUserAvatar(formData.Picture);
      }
    } else {
      // No picture set — select the first avatar by default
      const firstAvatar = Object.entries(OnboardingAvatars).find(
        ([, value]) => value !== "imagePicked",
      );
      if (firstAvatar) {
        const [key, value] = firstAvatar;
        setUserAvatar(key);
        convertAvatarToFile(key, value!);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync filtered job title list when professional descriptions load
  useEffect(() => {
    if (jobTitleList.length > 0) {
      setFilteredListOfJobTitle(jobTitleList);
    }
  }, [jobTitleList]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (!files || files.length === 0) return;

    const file = files[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Only image files are allowed.");
      event.target.value = "";
      return;
    }

    // Open the cropper instead of applying the file directly
    setCropperImageSrc(URL.createObjectURL(file));
    setIsCropperOpen(true);
    event.target.value = "";
  };

  const handleCropperSave = (croppedFile: File) => {
    const croppedUrl = URL.createObjectURL(croppedFile);
    setIsSelectedImage(croppedUrl);
    setUserAvatar("imagePicked");
    updateField("Picture", croppedFile);
  };

  const handleCropperDelete = () => {
    setIsSelectedImage(null);
    updateField("Picture", undefined);
    setIsCropperOpen(false);
    // Restore the first preset avatar
    const firstAvatar = Object.entries(OnboardingAvatars).find(
      ([, value]) => value !== "imagePicked",
    );
    if (firstAvatar) {
      const [key, value] = firstAvatar;
      setUserAvatar(key);
      convertAvatarToFile(key, value!);
    }
  };

  const handleCropperChangeImage = (_file: File, url: string) => {
    setCropperImageSrc(url);
  };

  // Clear error when user types
  const handleFullNameChange = (val: string) => {
    updateField("FullName", val);
    if (errors.fullName) {
      setErrors((prev) => ({ ...prev, fullName: "" }));
    }
  };

  // Helper functions to clear errors on dropdown selection
  const clearJobTitleError = () => {
    if (errors.jobTitle) {
      setErrors((prev) => ({ ...prev, jobTitle: "" }));
    }
  };

  const clearCountryError = () => {
    if (errors.country) {
      setErrors((prev) => ({ ...prev, country: "" }));
    }
  };

  const handleJobTitleChange: React.Dispatch<React.SetStateAction<string>> = (
    val,
  ) => {
    const newVal =
      typeof val === "function" ? val(formData.JobTitle || "") : val;
    updateField("JobTitle", newVal);
  };

  const handleLocationChange: React.Dispatch<React.SetStateAction<string>> = (
    val,
  ) => {
    const newVal =
      typeof val === "function" ? val(formData.Location || "") : val;
    updateField("Location", newVal);
  };

  const submitFirstPage = async () => {
    // Reset errors
    const newErrors = {
      fullName: "",
      jobTitle: "",
      country: "",
    };

    // Validate fields
    if (!formData.FullName?.trim()) {
      newErrors.fullName = "Full name is required";
    }

    if (!formData.Location) {
      newErrors.country = "Please select your location";
    }

    // Check if there are any errors
    if (Object.values(newErrors).some((error) => error !== "")) {
      setErrors(newErrors);
      return; // Don't proceed if there are errors
    }

    // Set derived fields and proceed
    setFormData((prev) => ({
      ...prev,
      FullName: prev.FullName || profileData?.fullName,
      Phone: profileData?.phoneNumber ?? undefined,
      JobId: professionalDescriptions?.find((d) => prev.JobTitle === d.title)
        ?.id,
    }));
    setIsFirstPage(false);
  };

  return (
    <>
      <div>
        {/* Full Name Field with error */}
        <GInput
          label="Full name"
          placeholder="Enter your name"
          setInput={handleFullNameChange}
          inputValue={formData.FullName || ""}
          required
          error={errors.fullName}
        />

        {/* Job Title Dropdown */}
        <div className="mt-5">
          <GInputWithDropDown
            label="What best Describes you?"
            placeHolder="E.g Coach or Consultant"
            filteredDataInArray={
              filteredListOfJobTitle.length > 0
                ? filteredListOfJobTitle
                : jobTitleList
            }
            setSelectedOption={handleJobTitleChange}
            OriginalDataInArray={jobTitleList}
            setFilteredArray={setFilteredListOfJobTitle}
            defaultValue={formData.JobTitle || ""}
            required
            error={errors.jobTitle}
            onSelect={clearJobTitleError} // Clear error on selection
          />
        </div>

        {/* Location Dropdown */}
        <div className="mt-5">
          <GInputWithDropDown
            label="Where are you based?"
            placeHolder="E.g United States"
            filteredDataInArray={filteredListOfCountries}
            setSelectedOption={handleLocationChange}
            OriginalDataInArray={countryList}
            setFilteredArray={setFilteredListOfCountries}
            defaultValue={formData.Location || ""}
            required
            error={errors.country}
            onSelect={clearCountryError} // Clear error on selection
          />
        </div>

        {/* Avatar Selection */}
        <p className="mt-5 text-[#344054] font-sfMedium sm:text-base text-sm">
          Select or upload an avatar
        </p>
        <div className="flex gap-1">
          <div className="grid grid-cols-5 gap-[0.7rem] sm:gap-4">
            {Object.entries(OnboardingAvatars).map(([key, value]) =>
              value !== "imagePicked" ? (
                <div
                  onClick={() => {
                    setUserAvatar(key);
                    convertAvatarToFile(key, value!);
                  }}
                  key={key}
                  className={`w-[50.5px] h-[50.5px] sm:w-[74px] sm:h-[74px] relative rounded-[100%] flex justify-center items-center bg-[#EEEEEE] cursor-pointer hover:scale-110 transition-transform duration-200  ${
                    key === userAvater &&
                    `border-[2.2px] sm:border-[3px] border-[#FF6929]`
                  }`}
                >
                  <img
                    src={value}
                    alt=""
                    className={` w-[50.5px] h-[50.5px] sm:w-[74px] sm:h-[74px]  `}
                  />
                </div>
              ) : (
                <div
                  onClick={() => {
                    setUserAvatar(key);
                  }}
                  key={key}
                  className={`w-[50.5px] h-[50.5px] sm:w-[74px] sm:h-[74px] relative rounded-[100%] flex justify-center items-center bg-[#EEEEEE] overflow-hidden cursor-pointer hover:scale-110 transition-transform duration-200 ${
                    key === userAvater &&
                    ` border-[2.2px] sm:border-[3px] border-[#FF6929]`
                  }`}
                >
                  <img
                    src={isSelectedImage || cameraIcon}
                    alt=""
                    className={`${
                      isSelectedImage
                        ? "object-cover w-full h-full rounded-[100%]"
                        : "object-contain"
                    } `}
                  />
                  <input
                    onChange={handleFileChange}
                    type="file"
                    accept="image/*"
                    name=""
                    id=""
                    className="absolute top-0 w-[50.5px] h-[50.5px] sm:w-[74px] sm:h-[74px] opacity-0 rounded-[100%]"
                  />
                </div>
              ),
            )}
          </div>
        </div>

        {/* Submit Button */}
        <div className="mt-5">
          <CButton
            text={"Next"}
            variant="dark"
            fullWidth
            action={submitFirstPage}
            disabled={
              !formData.FullName?.trim() ||
              !formData.Location ||
              !formData.JobTitle ||
              !formData.Picture
            } // Disable if loading or required fields are empty
          />
        </div>
      </div>

      {/* Image cropper modal – opens when the user picks a photo */}
      {isCropperOpen && cropperImageSrc && (
        <ImageCropperModal
          open={isCropperOpen}
          onClose={() => setIsCropperOpen(false)}
          imageSrc={cropperImageSrc}
          onSave={handleCropperSave}
          onDelete={handleCropperDelete}
          onChangeImage={handleCropperChangeImage}
        />
      )}
    </>
  );
};

export default OnbordingFirstMainBody;
